import { WorkflowStepStatuses, type WorkflowStep } from "~/api/types";
import { Badge } from "~/atoms/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/atoms/tooltip";
import { WorkflowStepBadge } from "~/molecules/WorkflowStepBadge";

interface MyWorkflowStepsCellProps {
  steps: WorkflowStep[] | undefined;
  currentUserId: number;
}

export const MyWorkflowStepsCell = ({
  steps,
  currentUserId,
}: MyWorkflowStepsCellProps) => {
  const userSteps =
    steps?.filter((step) =>
      step.users.some((u) => u.userId === currentUserId),
    ) ?? [];

  if (userSteps.length === 0) {
    return null;
  }

  const firstStep = userSteps[0];
  if (!firstStep) {
    return null;
  }

  const firstStepUser = firstStep.users.find((u) => u.userId === currentUserId);
  if (!firstStepUser) {
    return null;
  }

  const remainingSteps = userSteps.slice(1);

  return (
    <div className="flex items-center gap-1">
      <WorkflowStepBadge
        response={firstStepUser.responseStatus}
        isActive={firstStep.status === WorkflowStepStatuses.active}
        additionalClasses="min-w-0 max-w-36"
      >
        <span
          className="block min-w-0 truncate font-normal"
          title={firstStep.name}
        >
          {firstStep.name}
        </span>
      </WorkflowStepBadge>

      {remainingSteps.length > 0 && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="secondary"
                className="flex items-center gap-2 text-xs"
              >
                <span className="font-normal">+{remainingSteps.length}</span>
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="flex flex-col gap-1">
              {remainingSteps.map((step) => {
                const stepUser = step.users.find(
                  (u) => u.userId === currentUserId,
                );
                if (!stepUser) {
                  return null;
                }

                return (
                  <div key={step.id}>
                    <WorkflowStepBadge
                      response={stepUser.responseStatus}
                      isActive={step.status === WorkflowStepStatuses.active}
                      additionalClasses="relative min-w-0 max-w-56"
                    >
                      <span
                        className="block min-w-0 truncate font-normal"
                        title={step.name}
                      >
                        {step.name}
                      </span>
                    </WorkflowStepBadge>
                  </div>
                );
              })}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};
